import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Checkbox } from '../../components/UI/Checkbox/Checkbox';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import {
  AudioBlockData,
  ButtonBlockData,
  Course,
  CourseModule,
  HeadingBlockData,
  ImageBlockData,
  Lesson,
  LessonBlock,
  LessonBlockData,
  ParagraphBlockData,
  VideoBlockData,
} from '../../models/courses';

import { SortableBlock } from './components/blocks/SortableBlock';
import { BLOCK_META, BLOCK_ORDER, BlockType } from './components/blocks/blockMeta';
import { VideoBlock } from './components/blocks/VideoBlock';
import { ParagraphBlock } from './components/blocks/ParagraphBlock';
import { ImageBlock } from './components/blocks/ImageBlock';
import { AudioBlock } from './components/blocks/AudioBlock';
import { HeadingBlock } from './components/blocks/HeadingBlock';
import { ButtonBlock } from './components/blocks/ButtonBlock';
import {
  GetcourseImportDialog,
  ImportOptions,
} from './import/GetcourseImportDialog';
import { ImportResult, isRehostableImage } from './import/getcourseImport';
import styles from './LessonEditor.module.css';

interface LessonEditorProps {
  course: Course;
  module: CourseModule;
  lesson: Lesson;
  onBack: () => void;
  onSaved: () => void;
  onBackToCourses: () => void;
  onBackToModules: () => void;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function createEmptyBlock(type: BlockType): LessonBlock {
  const id = generateId();
  switch (type) {
    case 'video':
      return {
        id,
        type,
        data: { provider: 'kinescope', video_id: '', caption: '' },
      };
    case 'paragraph':
      return { id, type, data: { text: '' } };
    case 'image':
      return { id, type, data: { src: '', alt: '', caption: '' } };
    case 'audio':
      return { id, type, data: { src: '', caption: '' } };
    case 'heading':
      return { id, type, data: { level: 2, text: '' } };
    case 'button':
      return { id, type, data: { text: '', url: '' } };
    default: {
      const _exhaustive: never = type;
      throw new Error(`Unknown block type: ${_exhaustive}`);
    }
  }
}

const LessonEditor: React.FC<LessonEditorProps> = ({
  course,
  module,
  lesson,
  onBack,
  onSaved,
  onBackToCourses,
  onBackToModules,
}) => {
  const api = useApi();
  const store = useStores();

  const [title, setTitle] = useState<string>(lesson.title);
  const [durationMinutes, setDurationMinutes] = useState<number>(
    lesson.duration_minutes ?? 0,
  );
  const [isPreview, setIsPreview] = useState<boolean>(
    lesson.is_preview ?? false,
  );
  const [blocks, setBlocks] = useState<LessonBlock[]>(
    lesson.content?.blocks ?? [],
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [importOpen, setImportOpen] = useState<boolean>(false);
  // Прогресс перекачки картинок после импорта; пока идёт — сохранять нельзя.
  const [rehost, setRehost] = useState<{ done: number; total: number } | null>(
    null,
  );
  const busy = saving || rehost !== null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    setDurationMinutes(isNaN(val) ? 0 : val);
  };

  const handleAddBlock = (type: BlockType) => {
    setBlocks((prev) => [...prev, createEmptyBlock(type)]);
  };

  const handleUpdateBlock = (index: number, data: LessonBlockData) => {
    setBlocks((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], data };
      return updated;
    });
  };

  const handleDeleteBlock = (id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setBlocks((prev) => {
      const oldIndex = prev.findIndex((b) => b.id === active.id);
      const newIndex = prev.findIndex((b) => b.id === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // Блоки урока хранятся как произвольный JSON и не резолвятся бэкендом при
  // чтении, поэтому в data.src кладём готовый абсолютный url, а не key.
  const handleUploadImage = async (file: File): Promise<string> => {
    const { url } = await api.upload.image(file);
    return url;
  };

  const handleUploadAudio = async (file: File): Promise<string> => {
    const { url } = await api.upload.audio(file);
    return url;
  };

  // Картинки из GetCourse перекачиваем в S3 по одной; при ошибке блок остаётся
  // со ссылкой на GetCourse, админ может перезалить его вручную.
  const rehostImages = async (imported: LessonBlock[]) => {
    const targets = imported.filter(
      (b) =>
        b.type === 'image' && isRehostableImage((b.data as ImageBlockData).src),
    );
    if (targets.length === 0) {
      return;
    }
    let failed = 0;
    setRehost({ done: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      const block = targets[i];
      try {
        const { url } = await api.upload.imageByUrl(
          (block.data as ImageBlockData).src,
        );
        setBlocks((prev) =>
          prev.map((b) =>
            b.id === block.id
              ? { ...b, data: { ...(b.data as ImageBlockData), src: url } }
              : b,
          ),
        );
      } catch {
        failed++;
      }
      setRehost({ done: i + 1, total: targets.length });
    }
    setRehost(null);
    if (failed > 0) {
      store.notificationsStore.setError(
        `Не удалось перекачать картинок: ${failed} из ${targets.length} — они остались ссылками на GetCourse, загрузите их вручную.`,
      );
    }
  };

  const createAssignmentDraft = async (text: string) => {
    try {
      const existing = await api.assignments.getByLesson(lesson.id);
      if (existing) {
        store.notificationsStore.setError(
          'У урока уже есть задание — черновик из GetCourse не создан.',
        );
        return;
      }
      await api.assignments.create(lesson.id, {
        title: 'Задание',
        description: text,
        is_published: false,
        questions: [
          {
            type: 'free_text',
            question_text: 'Ваш ответ',
            is_required: true,
            options: [],
          },
        ],
      });
      store.notificationsStore.setNotifications(
        'Черновик ДЗ создан — проверьте и опубликуйте его в разделе заданий урока.',
      );
    } catch {
      store.notificationsStore.setError('Не удалось создать черновик ДЗ');
    }
  };

  const handleImport = (result: ImportResult, options: ImportOptions) => {
    setImportOpen(false);
    setBlocks((prev) =>
      options.replaceBlocks ? result.blocks : [...prev, ...result.blocks],
    );
    if (options.useTitle && result.title) {
      setTitle(result.title);
    }
    if (options.createAssignment && result.assignmentText) {
      createAssignmentDraft(result.assignmentText);
    }
    rehostImages(result.blocks);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      store.notificationsStore.setError('Название урока обязательно');
      return;
    }

    try {
      setSaving(true);
      await api.courses.updateLesson(
        {
          title,
          duration_minutes: durationMinutes,
          is_preview: isPreview,
          content: { version: 1, blocks },
        },
        lesson.id,
      );
      onSaved();
    } catch {
      store.notificationsStore.setError('Ошибка при сохранении урока');
    } finally {
      setSaving(false);
    }
  };

  const renderBlockEditor = (block: LessonBlock, index: number) => {
    switch (block.type) {
      case 'video':
        return (
          <VideoBlock
            data={block.data as VideoBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            disabled={busy}
          />
        );
      case 'paragraph':
        return (
          <ParagraphBlock
            data={block.data as ParagraphBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            disabled={busy}
          />
        );
      case 'image':
        return (
          <ImageBlock
            data={block.data as ImageBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            onUpload={handleUploadImage}
            disabled={busy}
          />
        );
      case 'audio':
        return (
          <AudioBlock
            data={block.data as AudioBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            onUpload={handleUploadAudio}
            disabled={busy}
          />
        );
      case 'heading':
        return (
          <HeadingBlock
            data={block.data as HeadingBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            disabled={busy}
          />
        );
      case 'button':
        return (
          <ButtonBlock
            data={block.data as ButtonBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            disabled={busy}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.editor}>
      <div className={styles.breadcrumb}>
        <span className={styles.crumb} onClick={onBackToCourses}>
          Курсы
        </span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumb} onClick={onBackToModules}>
          {course.title}
        </span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumb} onClick={onBack}>
          {module.title}
        </span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumbCurrent}>{lesson.title}</span>
      </div>

      {/* Мета-данные урока */}
      <div className={styles.meta}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Название урока</span>
          <input
            className={styles.titleInput}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Введите название урока"
            disabled={busy}
          />
        </div>
        <div className={styles.metaRow}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Длительность</span>
            <div className={styles.durationWrap}>
              <input
                className={styles.durationInput}
                type="number"
                min={0}
                value={durationMinutes}
                onChange={handleDurationChange}
                disabled={busy}
              />
              <span>мин</span>
            </div>
          </div>
          <div className={styles.toggleWrap}>
            <Checkbox
              label="Превью (доступен без покупки)"
              checked={isPreview}
              onChange={(e) => setIsPreview(e.target.checked)}
              disabled={busy}
            />
          </div>
        </div>
      </div>

      {/* Блоки контента */}
      <div className={styles.sectionLabel}>Контент урока</div>

      <div className={styles.toolbar}>
        {BLOCK_ORDER.map((type) => {
          const meta = BLOCK_META[type];
          const Icon = meta.icon;
          return (
            <button
              key={type}
              type="button"
              className={styles.toolbarBtn}
              onClick={() => handleAddBlock(type)}
              disabled={busy}
            >
              <Icon />
              {meta.label}
            </button>
          );
        })}
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={() => setImportOpen(true)}
          disabled={busy}
          title="Вставить урок, скопированный расширением со страницы GetCourse"
        >
          ⤓ Из GetCourse
        </button>
      </div>

      {rehost && (
        <div className={styles.importProgress}>
          Перекачиваем картинки из GetCourse: {rehost.done} из {rehost.total}…
        </div>
      )}

      <GetcourseImportDialog
        open={importOpen}
        hasBlocks={blocks.length > 0}
        genId={generateId}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
      />

      {blocks.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>Пока пусто</span>
          <span className={styles.emptyHint}>
            Добавьте первый блок из панели выше — он появится здесь.
          </span>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className={styles.blocks}>
              {blocks.map((block, index) => (
                <SortableBlock
                  key={block.id}
                  id={block.id}
                  type={block.type}
                  onDelete={() => handleDeleteBlock(block.id)}
                  disabled={busy}
                >
                  {renderBlockEditor(block, index)}
                </SortableBlock>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Sticky-панель действий */}
      <div className={styles.actionBar}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onBack}
          disabled={busy}
        >
          Отмена
        </button>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={busy}
        >
          {busy ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Сохранить'
          )}
        </button>
      </div>
    </div>
  );
};

export default LessonEditor;
